import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Clock3 } from "lucide-react";

const pad = (value) => String(value).padStart(2, "0");
const hours = Array.from({ length: 12 }, (_, index) => index + 1);
const minutes = Array.from({ length: 60 }, (_, index) => index);
const periods = ["AM", "PM"];

function parseTime(value) {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(value || ""));
  if (!match) return null;
  const hour24 = Number(match[1]);
  return { hour: hour24 % 12 || 12, minute: Number(match[2]), period: hour24 < 12 ? "AM" : "PM" };
}

function currentTime() {
  const now = new Date();
  return { hour: now.getHours() % 12 || 12, minute: now.getMinutes(), period: now.getHours() < 12 ? "AM" : "PM" };
}

export function formatTimePickerLabel(value) {
  const time = parseTime(value);
  return time ? `${pad(time.hour)}:${pad(time.minute)} ${time.period}` : "Select time";
}

function Wheel({ label, values, value, onChange, wrap = true, focusRef }) {
  const startY = useRef(null);
  const currentIndex = values.indexOf(value);
  const optionAt = (offset) => {
    const index = currentIndex + offset;
    return wrap ? values[(index + values.length) % values.length] : values[index];
  };
  const move = (direction) => {
    const next = optionAt(direction);
    if (next !== undefined) onChange(next);
  };

  return <div
    ref={focusRef}
    role="group"
    aria-label={label}
    tabIndex={0}
    onWheel={(event) => { if (event.deltaY) move(event.deltaY > 0 ? 1 : -1); }}
    onTouchStart={(event) => { startY.current = event.touches[0]?.clientY ?? null; }}
    onTouchEnd={(event) => {
      if (startY.current === null) return;
      const delta = startY.current - (event.changedTouches[0]?.clientY ?? startY.current);
      if (Math.abs(delta) > 16) move(delta > 0 ? 1 : -1);
      startY.current = null;
    }}
    onKeyDown={(event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      move(event.key === "ArrowDown" ? 1 : -1);
    }}
    className="relative z-10 grid h-[260px] min-w-0 grid-rows-5 outline-none focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-neutral-400"
  >
    {[-2, -1, 0, 1, 2].map((offset) => {
      const option = optionAt(offset);
      return option === undefined ? <span key={offset} /> : <button
        key={offset}
        type="button"
        aria-label={`${label} ${typeof option === "number" ? pad(option) : option}`}
        aria-pressed={offset === 0}
        onClick={() => onChange(option)}
        className={`h-[52px] w-full text-center text-2xl tabular-nums transition-colors ${offset === 0 ? "font-medium text-neutral-950 dark:text-white" : Math.abs(offset) === 1 ? "text-neutral-400 hover:text-neutral-700 dark:text-neutral-500 dark:hover:text-neutral-200" : "text-neutral-300 hover:text-neutral-600 dark:text-neutral-700 dark:hover:text-neutral-300"}`}
      >{typeof option === "number" ? pad(option) : option}</button>;
    })}
  </div>;
}

export default function TimePickerInput({ value, onValueChange, className = "", ariaLabel = "Time", disabled = false, required = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => parseTime(value) || currentTime());
  const dialogRef = useRef(null);
  const hourRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => hourRef.current?.focus());
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); setOpen(false); }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll('[tabindex="0"], button:not([disabled])');
      if (!focusable?.length) return;
      if (event.shiftKey && document.activeElement === focusable[0]) { event.preventDefault(); focusable[focusable.length - 1].focus(); }
      else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) { event.preventDefault(); focusable[0].focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [open]);

  const save = () => {
    const hour24 = (draft.hour % 12) + (draft.period === "PM" ? 12 : 0);
    onValueChange(`${pad(hour24)}:${pad(draft.minute)}`);
    setOpen(false);
  };

  return <>
    <button
      type="button"
      disabled={disabled}
      aria-label={`${ariaLabel}${required ? " (required)" : ""}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => { setDraft(parseTime(value) || currentTime()); setOpen(true); }}
      className={`${className} flex items-center justify-between gap-3 text-left disabled:cursor-not-allowed disabled:opacity-60`}
    ><span className={value ? "" : "text-neutral-400"}>{formatTimePickerLabel(value)}</span><Clock3 className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" /></button>
    {open && createPortal(<div className="fixed inset-0 z-[260] flex items-center justify-center bg-black/55 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Select ${ariaLabel.toLowerCase()}`} className="w-full max-w-[340px] rounded-[26px] border border-neutral-200 bg-white px-6 pb-5 pt-7 text-neutral-950 shadow-2xl dark:border-neutral-700 dark:bg-neutral-900 dark:text-white">
        <h2 className="text-center text-2xl font-medium">Select time</h2>
        <div className="relative mt-4 grid grid-cols-[1fr_1fr_1fr] gap-1">
          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-[52px] -translate-y-1/2 rounded-xl border border-neutral-200 bg-neutral-50 shadow-sm dark:border-neutral-700 dark:bg-neutral-800" aria-hidden="true" />
          <Wheel label="Hour" values={hours} value={draft.hour} onChange={(hour) => setDraft((current) => ({ ...current, hour }))} focusRef={hourRef} />
          <div className="relative z-10"><span className="pointer-events-none absolute left-0 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 text-2xl text-neutral-400">:</span><Wheel label="Minute" values={minutes} value={draft.minute} onChange={(minute) => setDraft((current) => ({ ...current, minute }))} /></div>
          <Wheel label="AM or PM" values={periods} value={draft.period} onChange={(period) => setDraft((current) => ({ ...current, period }))} wrap={false} />
        </div>
        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setOpen(false)} className="rounded-xl px-4 py-2.5 text-base hover:bg-neutral-100 dark:hover:bg-neutral-800">Cancel</button><button type="button" onClick={save} className="rounded-xl border border-neutral-300 px-4 py-2.5 text-base font-medium hover:bg-neutral-100 dark:border-neutral-600 dark:hover:bg-neutral-800">Save</button></div>
      </div>
    </div>, document.body)}
  </>;
}
